import { Box, Button, MenuItem, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { BadgeOutlined, PersonOutline } from "@mui/icons-material";
import { UseFormReturn } from "react-hook-form";
import { useRecords } from "../../api/useRecords";
import { STALE_TIME_24H } from "../../utils/constants";
import { CalenderFilterSelectField } from "./CalenderFilterSelectField";
import { TechnicianFilterMode } from "./calendar.types";

const TEAM_SHORTCUTS: Array<{ specialization: string; label: string }> = [
    { specialization: "neurofeedback", label: "Relacionado com Neurofeedback" },
    { specialization: "psicologia", label: "Relacionado com Psicologia" },
    { specialization: "avaliação", label: "Relacionado com Avaliações" },
    { specialization: "client_owner", label: "Relacionado com Gestão de Caso" },
];

const TECHNICIAN_FIELDS = "id,disabled,specializations,user_id.first_name,user_id.last_name";

type CalendarTechnicianFilterProps = {
    entityForm: UseFormReturn<any>;
};

export const filterTechnicianIdsBySpecialization = (
    technicians: Array<any>,
    specialization: string
) => {
    const matchingIds = technicians
        .filter((technician) => technician?.specializations?.includes(specialization))
        .map((technician) => technician.id);
    return matchingIds;
};

export const CalendarTechnicianFilter = ({ entityForm }: CalendarTechnicianFilterProps) => {
    const { status, data } = useRecords("technicians", TECHNICIAN_FIELDS, {
        extraOptions: { sort: "user_id.first_name" },
        fetch: { fetchOnFocus: false },
        staleTime: STALE_TIME_24H,
    });

    const watchedMode = entityForm.watch("technicianFilterMode") as TechnicianFilterMode | undefined;
    const technicianFilterMode: TechnicianFilterMode = watchedMode ?? "session";
    const isClientOwnerMode = technicianFilterMode === "client_owner";
    const fieldLabel = isClientOwnerMode ? "Gestor(a) de Caso" : "Técnico(a)";

    const handleModeChange = (
        _event: React.MouseEvent<HTMLElement>,
        newMode: TechnicianFilterMode | null
    ) => {
        if (newMode === null) return;
        entityForm.setValue("technicianFilterMode", newMode);
    };

    if (status !== "success") return null;

    const enabledTechnicians = data?.items.filter((technician: any) => technician.disabled !== true) || [];

    const handleSelectTeam = (specialization: string) => {
        const matchingIds = filterTechnicianIdsBySpecialization(enabledTechnicians, specialization);
        entityForm.setValue("technician", matchingIds);
    };

    const teamShortcutsMenu = TEAM_SHORTCUTS.map((shortcut) => (
        <MenuItem key={shortcut.specialization} disableRipple sx={{ "&:hover": { backgroundColor: "transparent" } }}>
            <Button
                fullWidth
                variant="contained"
                color="secondary"
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    handleSelectTeam(shortcut.specialization);
                }}
            >
                {shortcut.label}
            </Button>
        </MenuItem>
    ));

    return (
        <Box display="flex" flexDirection="row" alignItems="stretch" gap={0.5}>
            <ToggleButtonGroup
                orientation="vertical"
                exclusive
                size="small"
                value={technicianFilterMode}
                onChange={handleModeChange}
            >
                <Tooltip title="Filtrar por técnico(a) da sessão" placement="left">
                    <ToggleButton value="session" sx={{ p: 0.5 }}>
                        <PersonOutline fontSize="small" />
                    </ToggleButton>
                </Tooltip>
                <Tooltip title="Filtrar por gestor(a) de caso" placement="left">
                    <ToggleButton value="client_owner" sx={{ p: 0.5 }}>
                        <BadgeOutlined fontSize="small" />
                    </ToggleButton>
                </Tooltip>
            </ToggleButtonGroup>
            <Box flex={1} minWidth={0}>
                <CalenderFilterSelectField
                    entityForm={entityForm}
                    fieldName="technician"
                    label={fieldLabel}
                    options={enabledTechnicians}
                    propertyToMatch="id"
                    bottomActions={teamShortcutsMenu}
                />
            </Box>
        </Box>
    );
};
